import { Button } from "@/components/ui";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MAX_QUEUE_SIZE } from "@/constants";
import { useHandler, useShake } from "@/hooks";
import { useInputFocus, usePersistedPrompts } from "@/hooks";
import { useToast } from "@/hooks/use-toast";
import { processAndAddFiles } from "@/lib/fileUtils";
import { useCancellableMessages } from "@/lib/hooks/useCancellableMessages";
import { useIsMessageGenerating } from "@/lib/hooks/useIsMessageGenerating";
import { useWebSocketMessaging } from "@/lib/hooks/useWebSocketMessaging";
import { cn } from "@/lib/utils";
import {
  useAttachmentStore,
  useInputStore,
  useMessageStore,
  useQueuedPromptsStore,
  useRefInputStore,
} from "@/state/stores";
import { ArrowUp, Clock, Code2Icon, Paperclip, X } from "lucide-react";
import { memo, useEffect, useRef, useState } from "react";
import { SUPPORTED_FILE_EXTENSIONS } from "../../../../lib";
import styles from "./ChatInput.module.css";
import { Drawer, DrawerTabs } from "./Drawer";
import { QueuedPrompts } from "./QueuedPrompts";
import {
  UserAttachmentsDndOverlay,
  UserAttachmentsSection,
} from "./UserAttachments";
import {
  useAutoResizeTextArea,
  useIsNewProject,
  usePlaceholderAnimation,
  useTextChangeVisual,
} from "./hooks";

// Generate accept string from supported extensions (moved outside component for performance)
const ACCEPT_STRING = ["text/*", ...SUPPORTED_FILE_EXTENSIONS].join(",");

type ChatInputProps = {
  onSendMessage: () => void;
};

export const ChatInput = memo(function ChatInput({
  onSendMessage,
}: ChatInputProps) {
  const isNewProject = useIsNewProject();

  return (
    <div
      className={cn(
        "sticky bottom-0 left-0 mx-auto pt-5 bg-background/70 backdrop-blur-lg w-full",
        styles.chatInputContainer,
      )}
    >
      <div className="max-w-2xl mx-auto px-5 grid grid-cols-[minmax(0,1fr)] gap-2 pb-5">
        <Drawer />
        <QueuedPrompts />
        <Input onSendMessage={onSendMessage} />
        {isNewProject && <TemplatePrompts />}
      </div>
    </div>
  );
});

type InputProps = {
  onSendMessage: () => void;
};

export const Input = memo(function Input({ onSendMessage }: InputProps) {
  const isMessageGenerating = useIsMessageGenerating();
  const message = useInputStore((state) => state.message);
  const setMessage = useInputStore((state) => state.setMessage);
  const clearInput = useInputStore((state) => state.clearInput);
  const textareaRef = useRefInputStore(({ inputRef }) => inputRef);
  const hasMessages = useMessageStore((state) => state.messages.length > 0);
  const hasAttachments = useAttachmentStore((state) => state.files.length > 0);
  const isQueueAtLimit = useQueuedPromptsStore((store) => store.isAtQueueLimit);
  const { sendMessage, createNewProject, connectionStatus, reconnect } =
    useWebSocketMessaging();
  const showMessageValue = useShowMessageValue();
  const focusInput = useInputFocus();

  // Track if we've ever been connected to avoid showing disconnection UI on initial load
  const hasBeenConnected = useRef(false);
  const [showDisconnectionOverlay, setShowDisconnectionOverlay] =
    useState(false);

  const { setPersistedPrompt, addToPromptQueue, clearPersistedPrompt } =
    usePersistedPrompts({ processQueue: true });

  useTextChangeVisual();
  useAutoResizeTextArea();
  usePlaceholderAnimation();

  // Track connection state changes
  useEffect(() => {
    if (connectionStatus === "connected") {
      hasBeenConnected.current = true;
      setShowDisconnectionOverlay(false);
    } else if (hasBeenConnected.current) {
      // Show disconnection overlay for any non-connected state after being connected
      // This includes: disconnected, connecting (during reconnect), and error states
      setShowDisconnectionOverlay(true);
    }
  }, [connectionStatus]);

  // Reset height when processing starts
  useEffect(() => {
    if (isMessageGenerating) {
      return;
    }
    // Focus the textarea when processing is done
    focusInput();
  }, [focusInput, isMessageGenerating]);

  const handleMessageChange = useHandler(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setMessage(value);
      setPersistedPrompt(value);
    },
  );

  const handleSubmit = useHandler(async () => {
    const trimmedMessage = message.trim();

    // Allow sending if there's a message or attachments, but not if generating
    if (!trimmedMessage && !hasAttachments) {
      return;
    }

    if (isMessageGenerating) {
      if (trimmedMessage && !isQueueAtLimit) {
        await addToPromptQueue(trimmedMessage);
      }
      // If at limit, we don't add to queue, but we should still clear input
      // The shake animation and tooltip are handled in the InputButtons
      // component
      clearInput();
      return;
    }

    try {
      // Clear persisted draft immediately. If sending the message fails, the
      // draft will be restored after setting the message by the error handler.
      clearPersistedPrompt();

      if (hasMessages) {
        sendMessage(trimmedMessage);
      } else {
        await createNewProject(trimmedMessage);
      }

      onSendMessage();
    } catch (error) {
      console.error("Error sending message:", error);
      // Restore the message if there was an error
      setMessage(trimmedMessage);
    }
  });

  const handleKeyDown = useHandler(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
  );

  return (
    <div
      onMouseUp={focusInput}
      aria-label="Chat input area"
      className={cn(
        "p-3 mx-auto flex flex-col gap-3 rounded-xl w-full text-left cursor-text relative",
        styles.chatInput,
      )}
    >
      {/* Disconnection overlay - only show if we were previously connected */}
      {showDisconnectionOverlay && (
        <div className="absolute inset-0 bg-background/90 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
          <div className="flex flex-col items-center gap-2">
            {connectionStatus === "connecting" ? (
              <p className="text-sm text-muted-foreground">Reconnecting...</p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">Connection lost</p>
                <Button onClick={reconnect} variant="outline" size="sm">
                  Retry connection
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      <UserAttachmentsDndOverlay />
      <UserAttachmentsSection />
      <textarea
        ref={textareaRef}
        value={showMessageValue ? message : ""}
        onChange={handleMessageChange}
        onKeyDown={handleKeyDown}
        placeholder="Ask Fiberplane..."
        className="[all:unset] placeholder:text-muted-foreground focus:outline-none"
        disabled={showDisconnectionOverlay}
      />

      <div className="flex justify-between items-center mt-1">
        <DrawerTabs />

        <InputButtons
          isMessageGenerating={isMessageGenerating}
          handleSubmit={handleSubmit}
          isDisconnected={showDisconnectionOverlay}
        />
      </div>
    </div>
  );
});

type InputButtonsProps = {
  isMessageGenerating: boolean;
  handleSubmit: () => void;
  isDisconnected?: boolean;
};

const InputButtons = memo(function InputButtons({
  isMessageGenerating,
  handleSubmit,
  isDisconnected = false,
}: InputButtonsProps) {
  const addFiles = useAttachmentStore((state) => state.addFiles);
  const { toast } = useToast();

  // File selection logic
  const handleFileSelect = useHandler(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) {
        return;
      }

      const result = await processAndAddFiles(files, addFiles);

      // Show user feedback for rejected files
      if (result.rejectedFiles > 0) {
        const rejectedText =
          result.rejectedFiles === 1 ? "file was" : "files were";
        toast({
          title: "Some files couldn't be attached",
          description: `${result.rejectedFiles} ${rejectedText} rejected. Only text files are supported.`,
          variant: "destructive",
        });
      }

      // Reset the input so the same file can be selected again
      e.target.value = "";
    },
  );

  return (
    <div className="flex items-center gap-2">
      <label className="relative">
        <input
          type="file"
          accept={ACCEPT_STRING}
          onChange={handleFileSelect}
          className="appearance-none absolute inset-0 opacity-0 cursor-pointer"
          disabled={isMessageGenerating}
          multiple
        />
        <Button
          size="icon-sm"
          className="bg-border cursor-pointer"
          aria-label="Attach files"
          disabled={isMessageGenerating || isDisconnected}
        >
          <Paperclip />
        </Button>
      </label>

      <ActionButton
        handleSubmit={handleSubmit}
        isDisconnected={isDisconnected}
      />
    </div>
  );
});

const ActionButton = memo(function ActionButton({
  handleSubmit,
  isDisconnected = false,
}: Pick<InputButtonsProps, "handleSubmit" | "isDisconnected">) {
  // Pull state directly from stores
  const isMessageGenerating = useIsMessageGenerating();
  const hasMessage = useInputStore((state) => state.message.trim().length > 0);
  const hasAttachments = useAttachmentStore((state) => state.files.length > 0);
  const isQueueAtLimit = useQueuedPromptsStore((s) => s.isAtQueueLimit);
  const { triggerShake, shakeClassName } = useShake();

  // Pull handlers from hooks
  const cancellableMessageIds = useCancellableMessages();
  const { cancelMessage } = useWebSocketMessaging();

  const handleCancel = useHandler(() => {
    // Cancel all processing messages
    for (const messageId of cancellableMessageIds) {
      cancelMessage(messageId);
    }
  });

  const handleQueueSubmit = useHandler(() => {
    if (isQueueAtLimit) {
      // Queue is at limit, trigger shake animation
      triggerShake();
      // Still call handleSubmit to clear input, but addToPromptQueue won't be
      // called due to limit check
    }
    handleSubmit();
  });

  if (!isMessageGenerating) {
    return (
      <Button
        size="icon-sm"
        className="bg-border"
        onClick={handleSubmit}
        disabled={(!hasMessage && !hasAttachments) || isDisconnected}
        aria-label="Send message"
      >
        <ArrowUp />
      </Button>
    );
  }

  if (!hasMessage) {
    return (
      <Button
        variant="default"
        size="icon-sm"
        className="bg-border"
        onClick={handleCancel}
        aria-label="Cancel message generation"
      >
        <X />
      </Button>
    );
  }

  return (
    <Tooltip open={isQueueAtLimit && hasMessage}>
      <TooltipTrigger asChild>
        <Button
          variant="default"
          size="icon-sm"
          className={cn("bg-border", shakeClassName)}
          onClick={handleQueueSubmit}
          aria-label={isQueueAtLimit ? "Queue limit reached" : "Add to queue"}
        >
          <Clock />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>Can't queue more than {MAX_QUEUE_SIZE} messages</p>
      </TooltipContent>
    </Tooltip>
  );
});

function TemplatePrompts() {
  const setMessage = useInputStore((state) => state.setMessage);
  const focusInput = useInputFocus();
  const { setPersistedPrompt } = usePersistedPrompts();

  const handleTemplateClick = (prompt: string) => {
    setMessage(prompt);
    setPersistedPrompt(prompt);
    focusInput();
  };

  return (
    <div className="flex gap-3 w-full">
      {TEMPLATE_PROMPTS.map((template) => (
        <Button
          key={template.id}
          variant="outline"
          className="flex items-center gap-2 flex-1 border-dashed border-border rounded-lg hover:bg-secondary"
          onClick={() => handleTemplateClick(template.prompt)}
        >
          <Code2Icon className="h-4 w-4" /> {template.label}
        </Button>
      ))}
    </div>
  );
}

function useShowMessageValue() {
  const hasMessages = useMessageStore((state) => state.messages.length > 0);
  const isLoading = useMessageStore((state) => state.isLoading);
  return hasMessages || !isLoading;
}

const TEMPLATE_PROMPTS = [
  {
    id: "weather-mcp",
    label: "Weather MCP",
    prompt:
      "Generate an MCP (Model Context Protocol) server for weather data with the following features:\n\n- Get current weather for a specific location (mocked data)\n- Get weather forecast for a specific location (mocked data)\n- Get weather history for a specific location (mocked data)\n- Use realistic but fake weather data - no third-party API calls required",
  },
  {
    id: "ai-goose-jokes",
    label: "AI Jokes",
    prompt:
      "Create an AI-powered goose joke teller API with the following features:\n\n- OpenAI API integration for generating goose-themed jokes\n- Environment variable and secret configuration for OpenAI API key\n- RESTful endpoints:\n  - GET /jokes/random - Get a random goose joke\n  - GET /jokes/category/:type - Get jokes by category (silly, puns, dad-jokes)\n  - POST /jokes/custom - Generate custom goose jokes based on user prompts\n- Rate limiting to manage OpenAI API costs\n- Response formatting with joke setup, punchline, and goose rating (1-5 honks)",
  },
  {
    id: "personal-crm-mcp",
    label: "Personal Contacts MCP",
    prompt:
      "Generate an MCP (Model Context Protocol) server for a personal CRM system with the following features:\n- Contact management (create, update, delete contacts)\n- Company name and role. Contact search and filtering capabilities. Store contact information in a d1 database.",
  },
  {
    id: "blog",
    label: "Blog API",
    prompt:
      "Create a RESTful blog API with the following features:\n\n- User authentication and authorization\n- CRUD operations for blog posts\n  - Each post should have: title, content (Markdown), author, tags, and publish date\n  - Support for draft/published status\n- Comment system\n  - Nested comments support\n  - Moderation capabilities",
  },
] as const;
